// Immutable conversation buckets with a sharded ID index. The read adapter
// preserves messages[id], Object.keys/values and spread for older consumers.
// A write copies one conversation, not every message in the working day.
const META = Symbol('conversation-message-map');
const EMPTY = Object.freeze({});
const shard = id => { let hash = 0; for (const letter of id) hash = (hash * 31 + letter.charCodeAt(0)) | 0; return hash & 255; };
export const identifierBucket = id => String(shard(id));
function adapter(buckets, index) {
  const lookup = id => typeof id === 'string' ? buckets[index[shard(id)]?.[id]]?.[id] : undefined;
  return new Proxy({}, {
    get: (_, id) => id === META ? { buckets, index } : lookup(id),
    has: (_, id) => lookup(id) !== undefined,
    ownKeys: () => index.flatMap(chunk => Object.keys(chunk || EMPTY)),
    getOwnPropertyDescriptor: (_, id) => lookup(id) === undefined ? undefined : { enumerable: true, configurable: true, value: lookup(id) },
  });
}
export const emptyMessageMap = () => adapter({}, []);
export function conversationMessages(messages, conversationId) {
  if (messages?.[META]) return messages[META].buckets[conversationId] || EMPTY;
  return Object.fromEntries(Object.entries(messages || EMPTY).filter(([, item]) => item.conversation_id === conversationId));
}
export function patchMessages(messages, updates = {}, removed = [], conversationHint) {
  if (!messages?.[META]) messages = patchMessages(emptyMessageMap(), messages || EMPTY);
  const previous = messages[META];
  const buckets = { ...previous.buckets }, index = previous.index.slice();
  const touched = new Set(), touchedShards = new Set();
  const touch = key => { if (!touched.has(key)) { buckets[key] = { ...buckets[key] }; touched.add(key); } };
  const touchIndex = id => { const slot = shard(id); if (!touchedShards.has(slot)) { index[slot] = { ...index[slot] }; touchedShards.add(slot); } return index[slot]; };
  for (const id of removed) {
    const key = index[shard(id)]?.[id];
    if (key === undefined) continue;
    touch(key); delete buckets[key][id]; delete touchIndex(id)[id];
  }
  for (const [id, item] of Object.entries(updates)) {
    const oldKey = index[shard(id)]?.[id];
    const key = conversationHint || item.conversation_id || oldKey || '__unknown__';
    if (oldKey !== undefined && oldKey !== key) { touch(oldKey); delete buckets[oldKey][id]; }
    touch(key); buckets[key][id] = item; touchIndex(id)[id] = key;
  }
  for (const key of touched) if (!Object.keys(buckets[key]).length) delete buckets[key];
  return adapter(buckets, index);
}
